import { useI18n } from "i18n";
import { selectUserId } from "modules/auth/redux/selectors";
import {
  VIRTUAL_BACKGROUND,
  UPLOAD_LEVEL,
} from "services/firebaseService/storagePaths";
import { useSelector } from "react-redux";
import { useBackgroundImageQuery } from "modules/audioVideo/hooks/useVirtualBackground/query";
import { ImageUploader } from "modules/uploader";
import { AddCircleOutline } from "@remo-co/ui-core/src/icons/AddCircleOutline";
import logger from "logging/logger";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { ComponentPropsWithRef, useCallback } from "react";
import { addErrorNotification } from "modules/notification/redux/notificationSlice";
import { Errors, trackError } from "modules/monitoring";
import { useAppDispatch } from "store/hooks";
import { MenuButton } from "./MenuButton";

const ImageUploadButtonBase = (props: ComponentPropsWithRef<typeof Button>) => {
  const { t } = useI18n(["videoBackground"]);

  return (
    <MenuButton
      {...props}
      icon={<AddCircleOutline />}
      label={t("videoBackground:button.upload.image")}
    />
  );
};

export const UploadImageButton = ({ disabled = false }) => {
  const { addBackground } = useBackgroundImageQuery();
  const userId = useSelector(selectUserId);
  const dispatch = useAppDispatch();
  const { t } = useI18n();

  const filePath = `${VIRTUAL_BACKGROUND}/${userId}`;

  const handleUploaded = (_: string, resultFilePath?: string) => {
    logger.info(
      `[virtualBackground][imageUpload] uploaded to: ${resultFilePath}`,
    );
    if (resultFilePath) {
      addBackground(resultFilePath);
    }
  };

  const handleError = useCallback(
    (error: Error) => {
      dispatch(
        addErrorNotification({
          message: t("eventForm:upload.image.error"),
          autoDismiss: 10,
        }),
      );
      trackError(error, { label: Errors.VIRTUAL_BACKGROUND });
    },
    [dispatch, t],
  );

  if (userId === null) {
    return null;
  }

  return (
    <ImageUploader
      button={ImageUploadButtonBase}
      disabled={disabled}
      name="backgroundImage"
      acceptedFileTypes={["image/jpeg", "image/png"]}
      level={UPLOAD_LEVEL.USER}
      path={filePath}
      onError={handleError}
      onImageUploaded={handleUploaded}
      maxFileSize="3MB"
      useUuidFileNames
    />
  );
};
